/**
 * Iter 209 — Modal de creación administrativa de solicitudes RRHH.
 *
 * Permite a super_admin/admin (con permiso `vacaciones/crear`) registrar una
 * solicitud de vacaciones / asuntos propios / permiso retribuido / baja médica
 * en nombre de un empleado, en dos modos:
 *   - on_behalf: entra al flujo normal de aprobación.
 *   - manual_approved: queda registrada como aprobada directamente
 *     (requiere motivo y doble confirmación).
 *
 * Endpoints usados:
 *   GET  /api/employees                                   (lista de empleados)
 *   GET  /api/admin/vacation-requests/employee-summary/:id
 *   GET  /api/rrhh/calculate-days?...                     (preview de días)
 *   POST /api/admin/vacation-requests/create-on-behalf    (alta)
 */
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, AlertTriangle, ShieldCheck, Search } from "lucide-react";

const MODES = [
  { value: "on_behalf", label: "Solicitar en nombre del empleado", hint: "Entra al flujo normal de aprobación (jefe directo / gerente)." },
  { value: "manual_approved", label: "Registrar como ya aprobado", hint: "Queda registrada directamente como aprobada. Requiere motivo." },
];

const TYPE_OPTIONS = [
  { value: "vacaciones", label: "Vacaciones" },
  { value: "asuntos_propios", label: "Asuntos propios" },
  { value: "permiso_retribuido", label: "Permiso retribuido" },
  { value: "baja_medica", label: "Baja médica" },
];

const parseApiError = (e, fallback = "Error") => {
  const detail = e?.response?.data?.detail;
  if (!detail) return e?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => {
      const loc = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
      const msg = d?.msg || "Error";
      return loc ? `${loc}: ${msg}` : msg;
    }).join(" | ");
  }
  return JSON.stringify(detail);
};

export default function AdminCreateRequestModal({ open, onOpenChange, axiosAuth, onCreated }) {
  const [employees, setEmployees] = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [search, setSearch] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [employeeSummary, setEmployeeSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [form, setForm] = useState({
    request_type: "vacaciones",
    start_date: "",
    end_date: "",
    comments: "",
    mode: "on_behalf",
    admin_reason: "",
    allow_negative_balance: false,
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false); // doble confirmación manual_approved

  // ── Carga inicial de empleados ──
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      try {
        setLoadingEmps(true);
        const r = await axiosAuth().get("/employees");
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        if (!cancel) setEmployees(items);
      } catch (e) {
        toast.error(parseApiError(e, "Error cargando empleados"));
      } finally {
        if (!cancel) setLoadingEmps(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, axiosAuth]);

  // ── Reset al abrir/cerrar ──
  useEffect(() => {
    if (!open) {
      setEmployeeId("");
      setEmployeeSummary(null);
      setSearch("");
      setForm({
        request_type: "vacaciones", start_date: "", end_date: "",
        comments: "", mode: "on_behalf", admin_reason: "",
        allow_negative_balance: false,
      });
      setPreview(null);
      setConfirmStep(false);
    }
  }, [open]);

  // ── Resumen del empleado seleccionado ──
  useEffect(() => {
    if (!employeeId) { setEmployeeSummary(null); return; }
    let cancel = false;
    (async () => {
      try {
        setLoadingSummary(true);
        const r = await axiosAuth().get(`/admin/vacation-requests/employee-summary/${employeeId}`);
        if (!cancel) setEmployeeSummary(r.data);
      } catch (e) {
        toast.error(parseApiError(e, "Error cargando datos del empleado"));
      } finally {
        if (!cancel) setLoadingSummary(false);
      }
    })();
    return () => { cancel = true; };
  }, [employeeId, axiosAuth]);

  // ── Preview de días: re-calcular si cambian fechas o tipo ──
  useEffect(() => {
    if (!form.start_date || !form.end_date || !form.request_type) {
      setPreview(null); return;
    }
    if (form.end_date < form.start_date) {
      setPreview({ error: "Fecha fin anterior a fecha inicio" }); return;
    }
    let cancel = false;
    (async () => {
      try {
        // Iter 209: el preview usa el mismo endpoint que el portal del empleado
        // para mantener consistencia con el cálculo final.
        const rtMap = { permiso_retribuido: "permiso" };
        const rt = rtMap[form.request_type] || form.request_type;
        const r = await axiosAuth().get(
          `/rrhh/calculate-days?request_type=${rt}&start_date=${form.start_date}&end_date=${form.end_date}`,
        );
        if (!cancel) setPreview(r.data);
      } catch (e) {
        if (!cancel) setPreview({ error: parseApiError(e, "Error en preview") });
      }
    })();
    return () => { cancel = true; };
  }, [form.start_date, form.end_date, form.request_type, axiosAuth]);

  const filteredEmployees = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => {
      const blob = [
        e.name, e.full_name, e.email, e.center, e.department, e.position,
      ].filter(Boolean).join(" ").toLowerCase();
      return blob.includes(q);
    });
  }, [employees, search]);

  const balance = useMemo(() => {
    if (!employeeSummary) return null;
    return employeeSummary.balances?.[form.request_type] || null;
  }, [employeeSummary, form.request_type]);

  const needsNegativeOverride = useMemo(() => {
    if (!balance || balance.available == null) return false;
    if (!preview || preview.error || preview.days == null) return false;
    return balance.available < preview.days;
  }, [balance, preview]);

  const canSubmit = useMemo(() => {
    if (!employeeId) return false;
    if (!form.start_date || !form.end_date) return false;
    if (form.end_date < form.start_date) return false;
    if (!preview || preview.error) return false;
    if (form.mode === "manual_approved" && !form.admin_reason.trim()) return false;
    if (needsNegativeOverride) {
      if (!form.allow_negative_balance) return false;
      if (!form.admin_reason.trim()) return false;
    }
    return true;
  }, [employeeId, form, preview, needsNegativeOverride]);

  const submit = async () => {
    // Si es manual_approved y aún no hemos pulsado "confirmar", abrir paso de confirmación
    if (form.mode === "manual_approved" && !confirmStep) {
      setConfirmStep(true);
      return;
    }
    try {
      setSaving(true);
      const payload = {
        employee_id: employeeId,
        request_type: form.request_type,
        start_date: form.start_date,
        end_date: form.end_date,
        comments: form.comments.trim(),
        mode: form.mode,
        admin_reason: form.admin_reason.trim(),
        allow_negative_balance: !!form.allow_negative_balance,
        confirm_manual_approved: form.mode === "manual_approved",
      };
      const r = await axiosAuth().post("/admin/vacation-requests/create-on-behalf", payload);
      toast.success(
        form.mode === "manual_approved"
          ? "Solicitud registrada como aprobada"
          : "Solicitud creada y enviada al flujo de aprobación",
      );
      onOpenChange(false);
      if (onCreated) onCreated(r.data);
    } catch (e) {
      toast.error(parseApiError(e, "Error al guardar"));
      setConfirmStep(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-create-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" /> Nueva solicitud por empleado
          </DialogTitle>
          <DialogDescription>
            Crea una solicitud RRHH en nombre de un empleado. Queda registrado en auditoría que la
            ha creado administración.
          </DialogDescription>
        </DialogHeader>

        {/* PASO 1: Selector de empleado */}
        <div className="space-y-2 border-b border-slate-200 pb-4">
          <Label className="text-[11px] uppercase tracking-wider text-slate-600">Empleado</Label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, email, centro o departamento…"
              className="pl-8 h-9 text-sm"
              data-testid="admin-create-search-input"
            />
          </div>
          <div className="border border-slate-200 rounded-sm max-h-40 overflow-y-auto" data-testid="admin-create-employee-list">
            {loadingEmps ? (
              <p className="text-xs text-slate-400 p-2">Cargando empleados…</p>
            ) : filteredEmployees.length === 0 ? (
              <p className="text-xs text-slate-400 p-2">Sin resultados</p>
            ) : filteredEmployees.slice(0, 30).map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => setEmployeeId(e.id)}
                className={`w-full text-left px-3 py-1.5 text-xs border-b border-slate-100 hover:bg-slate-50 ${employeeId === e.id ? "bg-blue-50 text-blue-900" : ""}`}
                data-testid={`admin-create-emp-${e.id}`}
              >
                <strong>{e.name || e.full_name || "(sin nombre)"}</strong>
                <span className="text-slate-500"> · {e.email || "—"}</span>
                <span className="text-slate-400 ml-2">{[e.center, e.department].filter(Boolean).join(" / ") || "—"}</span>
              </button>
            ))}
            {filteredEmployees.length > 30 && (
              <p className="text-[10px] text-slate-400 p-1.5 text-center bg-slate-50">
                Mostrando 30 de {filteredEmployees.length}. Refina la búsqueda…
              </p>
            )}
          </div>

          {/* Resumen empleado seleccionado */}
          {employeeId && (
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-2.5 text-xs" data-testid="admin-create-employee-summary">
              {loadingSummary || !employeeSummary ? (
                <p className="text-slate-400">Cargando datos del empleado…</p>
              ) : (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span><strong>{employeeSummary.employee.name}</strong></span>
                  <span className="text-slate-500">{employeeSummary.employee.email || "—"}</span>
                  <span className="text-slate-500">{employeeSummary.employee.center || "(sin centro)"} / {employeeSummary.employee.department || "(sin depto)"}</span>
                  {!employeeSummary.employee.direct_manager_id && (
                    <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]">⚠ Sin jefe directo</Badge>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* PASO 2: Datos de la solicitud */}
        <div className="space-y-3 border-b border-slate-200 pb-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-600">Tipo de ausencia</Label>
              <Select value={form.request_type} onValueChange={(v) => setForm((s) => ({ ...s, request_type: v }))}>
                <SelectTrigger data-testid="admin-create-type" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-600">Modo de alta</Label>
              <Select value={form.mode} onValueChange={(v) => { setForm((s) => ({ ...s, mode: v })); setConfirmStep(false); }}>
                <SelectTrigger data-testid="admin-create-mode" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 mt-1">{MODES.find((m) => m.value === form.mode)?.hint}</p>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-600">Fecha inicio</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm((s) => ({ ...s, start_date: e.target.value }))} className="h-9 text-sm" data-testid="admin-create-start" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-600">Fecha fin</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm((s) => ({ ...s, end_date: e.target.value }))} className="h-9 text-sm" data-testid="admin-create-end" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-600">Comentario / motivo (opcional)</Label>
            <Textarea rows={2} value={form.comments} onChange={(e) => setForm((s) => ({ ...s, comments: e.target.value }))} className="text-sm" data-testid="admin-create-comments" />
          </div>

          {/* Preview de días — Iter 218: claridad visual Días descontados vs Periodo natural */}
          {preview && (
            <div className={`rounded-sm border p-3 text-xs ${preview.error ? "bg-red-50 border-red-200 text-red-800" : "bg-blue-50 border-blue-200 text-blue-900"}`} data-testid="admin-create-preview">
              {preview.error ? (
                <span>⚠ {preview.error}</span>
              ) : (() => {
                const naturalP = preview.natural_days_in_period;
                const holsExc = Array.isArray(preview.holidays_excluded) ? preview.holidays_excluded : [];
                const weekendsExc = (preview.mode === "business_days" && naturalP != null && preview.days != null)
                  ? Math.max(0, naturalP - preview.days - holsExc.length)
                  : 0;
                const unit = preview.mode === "business_days" ? "laborables" : preview.mode === "natural_days" ? "naturales" : "días";
                const modeLabel = preview.mode === "business_days" ? "descuento laborable" : preview.mode === "natural_days" ? "días naturales" : "manual";
                const avail = balance?.available;
                const after = (avail != null && preview.days != null) ? (avail - preview.days) : null;
                const afterCls = (after != null && after < 0) ? "text-red-700 font-bold" : "text-blue-900 font-semibold";
                return (
                  <>
                    <div className="flex items-baseline flex-wrap gap-x-2">
                      <span
                        className="text-2xl font-extrabold leading-none text-blue-900"
                        data-testid="admin-create-preview-discounted"
                      >
                        {preview.days}
                      </span>
                      <span className="text-xs font-semibold text-blue-900">{unit} descontados</span>
                      <Badge variant="outline" className="text-[10px] ml-1" data-testid={`admin-create-mode-badge-${preview.mode}`}>
                        {modeLabel}
                      </Badge>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
                      {naturalP != null && (
                        <span>Periodo natural: <span className="font-mono font-semibold">{naturalP}</span> días</span>
                      )}
                      {avail != null && (
                        <span>
                          Saldo: <span className="font-mono font-semibold">{avail}</span>
                          <span className="mx-1">→</span>
                          <span className={`font-mono ${afterCls}`} data-testid="admin-create-balance-after">{after}</span>
                        </span>
                      )}
                      {holsExc.length > 0 && (
                        <span className="text-amber-700">Festivos excluidos: <strong>{holsExc.length}</strong></span>
                      )}
                      {weekendsExc > 0 && (
                        <span className="text-amber-700">Fines de semana excluidos: <strong>{weekendsExc}</strong></span>
                      )}
                      {needsNegativeOverride && (
                        <Badge className="bg-red-600 text-white text-[10px]">Saldo insuficiente</Badge>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* PASO 3: Avisos y campos según modo */}
        <div className="space-y-3">
          <div className="rounded-sm border border-blue-200 bg-blue-50 p-2.5 text-xs text-blue-900 flex gap-2" data-testid="admin-create-info">
            <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Esta solicitud quedará registrada como <strong>creada por administración</strong>. El empleado la verá en su portal con esa marca.</span>
          </div>

          {form.mode === "manual_approved" && (
            <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="admin-create-manual-approved-block">
              <div className="flex items-start gap-2 text-xs text-amber-900">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Modo <strong>&quot;Registrar como ya aprobado&quot;</strong>: la solicitud se guarda directamente como <strong>aprobada</strong>, sin pasar por jefe directo ni gerente. Esto descontará saldo si aplica.</span>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-amber-900">Motivo (obligatorio) *</Label>
                <Textarea
                  rows={2}
                  value={form.admin_reason}
                  onChange={(e) => setForm((s) => ({ ...s, admin_reason: e.target.value }))}
                  placeholder="Ej: acuerdo cerrado verbalmente con dirección antes del despliegue del portal…"
                  className="text-sm"
                  data-testid="admin-create-admin-reason"
                />
              </div>
            </div>
          )}

          {needsNegativeOverride && (
            <div className="rounded-sm border border-red-300 bg-red-50 p-3 space-y-2" data-testid="admin-create-negative-block">
              <p className="text-xs text-red-900">
                <strong>Saldo insuficiente.</strong> El empleado tiene <strong>{balance?.available}</strong> días disponibles pero la solicitud requiere <strong>{preview?.days}</strong>.
                Para forzar el alta, marca la casilla y proporciona un motivo claro.
              </p>
              <label className="flex items-center gap-2 text-xs text-red-900 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.allow_negative_balance}
                  onChange={(e) => setForm((s) => ({ ...s, allow_negative_balance: e.target.checked }))}
                  className="h-4 w-4 rounded-sm border-red-400"
                  data-testid="admin-create-allow-negative"
                />
                Permitir saldo negativo (quedará registrado en auditoría)
              </label>
              {form.mode !== "manual_approved" && form.allow_negative_balance && (
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-red-900">Motivo (obligatorio) *</Label>
                  <Textarea
                    rows={2}
                    value={form.admin_reason}
                    onChange={(e) => setForm((s) => ({ ...s, admin_reason: e.target.value }))}
                    className="text-sm"
                    data-testid="admin-create-negative-reason"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Confirmación final si manual_approved */}
        {confirmStep && form.mode === "manual_approved" && (
          <div className="rounded-sm border-2 border-red-400 bg-red-50 p-3 text-xs text-red-900" data-testid="admin-create-confirm-step">
            <p className="font-bold mb-1">¿Confirmas el registro como APROBADA?</p>
            <p>
              Vas a crear una solicitud de <strong>{TYPE_OPTIONS.find((t) => t.value === form.request_type)?.label}</strong>{" "}
              del <strong>{form.start_date}</strong> al <strong>{form.end_date}</strong>{" "}
              ({preview?.days} días) para <strong>{employeeSummary?.employee.name}</strong>.
              Se guardará <strong>directamente como aprobada</strong> en su historial.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="admin-create-cancel">Cancelar</Button>
          {confirmStep && form.mode === "manual_approved" ? (
            <>
              <Button variant="outline" onClick={() => setConfirmStep(false)} data-testid="admin-create-back">Atrás</Button>
              <Button
                onClick={submit}
                disabled={saving}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="admin-create-confirm-final"
              >
                {saving ? "Guardando…" : "Sí, registrar como APROBADA"}
              </Button>
            </>
          ) : (
            <Button
              onClick={submit}
              disabled={!canSubmit || saving}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="admin-create-submit"
            >
              {saving ? "Guardando…" : form.mode === "manual_approved" ? "Continuar (registrar como aprobada)" : "Crear solicitud"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
